/* @jsx h */
import { defineComponent, h, ref, computed } from "vue";
import { RightSheet, useLocalization } from "@lemmonhq/uikit";
import { useToast } from "@app/vue/providers/Toast";
import { useRejectRequestMutation } from "./composables/useReservationRequestMutations";
import { useAuth } from "@app/vue/providers/Auth";
import { ExternalLink } from "@app/vue/components/ExternalLink";
import { ReservationRequest, ReservationDeclineReason } from "@lemmonhq/types";

const getDeclineReasons = (
  t: (key: string) => string,
): {
  id: ReservationDeclineReason | "other";
  label: string;
  icon: string;
}[] => [
  {
    id: "fully_booked",
    label: t("reservations.reject.fully_booked"),
    icon: "event_busy",
  },
  {
    id: "party_too_large",
    label: t("reservations.reject.party_too_large"),
    icon: "group",
  },
  {
    id: "outside_hours",
    label: t("reservations.reject.outside_hours"),
    icon: "schedule",
  },
  {
    id: "table_unavailable",
    label: t("reservations.reject.table_unavailable"),
    icon: "table_restaurant",
  },
  {
    id: "area_fully_booked",
    label: t("reservations.reject.area_fully_booked"),
    icon: "space_dashboard",
  },
  { id: "other", label: t("reservations.reject.other"), icon: "edit_note" },
];

export interface RejectReservationRequestProps {
  request: ReservationRequest;
  isOpen: boolean;
  onClose: () => void;
  onReject?: (request: ReservationRequest) => void;
  onRejected: (updatedRequest?: ReservationRequest) => void;
}

export const RejectReservationRequest =
  defineComponent<RejectReservationRequestProps>({
    name: "RejectReservationRequest",
    props: ["request", "isOpen", "onClose", "onReject", "onRejected"],
    setup(props) {
      const toast = useToast();
      const { user } = useAuth();
      const { t } = useLocalization();
      const timezone = user.value?.restaurant?.timezone || "UTC";
      const declineReasons = computed(() => getDeclineReasons(t));
      const rejectMutation = useRejectRequestMutation();
      const submitting = ref(false);
      const selectedReason = ref<ReservationDeclineReason | "other" | null>(
        null,
      );
      const customMessage = ref("");

      const requestDate = computed(() => {
        const date = new Date(props.request.requested_time);
        return date.toLocaleDateString("en-US", {
          weekday: "long",
          month: "long",
          day: "numeric",
          timeZone: timezone,
        });
      });

      const requestTime = computed(() => {
        const date = new Date(props.request.requested_time);
        return date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          timeZone: timezone,
        });
      });

      const handleReject = async () => {
        try {
          submitting.value = true;
          const updatedRequest = await rejectMutation.mutate({
            request_id: props.request.id,
            status: "rejected",
            reason:
              selectedReason.value && selectedReason.value !== "other"
                ? selectedReason.value
                : undefined,
            message: customMessage.value.trim() || undefined,
          });
          toast.showToast(t("reservations.reject.rejected_success"), {
            type: "success",
            autoHide: 3000,
          });

          // Call onReject if provided
          if (props.onReject) {
            props.onReject(updatedRequest);
          }

          props.onRejected(updatedRequest);
          props.onClose();
        } catch (e) {
          console.error("Failed to decline request:", e);
          toast.showToast(t("reservations.reject.reject_failed"), {
            type: "error",
          });
        } finally {
          submitting.value = false;
        }
      };

      const resetState = () => {
        selectedReason.value = null;
        customMessage.value = "";
      };

      // Reset when sheet opens
      const handleClose = () => {
        resetState();
        props.onClose();
      };

      return () => (
        <RightSheet
          title={
            <span class="font-semibold text-accent text-base truncate">
              {props.request.party_name ||
                props.request.customer_name ||
                t("reservations.general.guest_label")}
            </span>
          }
          isOpen={props.isOpen}
          onClose={handleClose}
          width="520px"
          height="calc(100dvh - 2rem)"
          mobileVariant="full-screen"
        >
          <div class="flex flex-col h-full min-h-0 overflow-hidden">
            {/* Content */}
            <div class="flex-1 min-h-0 overflow-y-auto">
              <div class="p-4 space-y-4">
                {/* Warning Banner */}
                <div class="rounded-2xl bg-error/100 border border-error/100 p-3 flex items-start gap-3 l-box-shadow">
                  <div class="w-10 h-10 rounded-full bg-error flex items-center justify-center text-accent">
                    <span class="material-symbols-outlined">cancel</span>
                  </div>
                  <div>
                    <div class="font-semibold text-accent text-sm">
                      {t("reservations.reject.decline_question")}
                    </div>
                    <div class="text-xs text-accent">
                      {t("reservations.reject.guest_will_be_notified")}
                    </div>
                  </div>
                </div>

                {/* Request Summary */}
                <div class="rounded-2xl border border-base-300 bg-gradient-to-r from-base-200 via-base-200/70 to-base-100 p-4 l-box-shadow">
                  <div class="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div class="text-xs font-semibold text-primary uppercase tracking-wide">
                        {requestDate.value}
                      </div>
                      <div class="text-2xl text-accent mt-1 leading-none">
                        {requestTime.value}
                      </div>
                    </div>
                    <div class="flex items-center gap-3 text-sm text-base-content/70">
                      <span class="inline-flex items-center gap-1.5 px-3 py-1 text-accent font-semibold">
                        <span class="material-symbols-outlined text-lg">
                          group
                        </span>
                        {props.request.party_size}{" "}
                        {props.request.party_size === 1
                          ? t("reservations.general.guest")
                          : t("reservations.general.guests")}
                      </span>
                      <span class="inline-flex items-center gap-1.5 px-3 py-1 text-accent font-semibold">
                        <span class="material-symbols-outlined text-lg">
                          schedule
                        </span>
                        {props.request.duration_mins} min
                      </span>
                    </div>
                  </div>
                </div>

                {/* Guest Information & Contact */}
                <div class="space-y-3">
                  <div class="flex items-start gap-3">
                    <div class="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
                      <span class="material-symbols-outlined text-primary text-xl">
                        person
                      </span>
                    </div>
                    <div>
                      <div class="font-semibold text-accent text-base">
                        {props.request.party_name ||
                          props.request.customer_name ||
                          t("reservations.general.guest_label")}
                      </div>
                      <div class="text-xs text-base-content/50 uppercase tracking-wide">
                        {t("reservations.general.guest_label")}
                      </div>
                    </div>
                  </div>
                  <div class="grid grid-cols-1 gap-2">
                    {(() => {
                      const phone = props.request.party_phone;
                      const isDisabled = !phone;
                      return (
                        <ExternalLink
                          class={`btn btn-primary w-full justify-start gap-2 ${
                            isDisabled
                              ? "btn-disabled pointer-events-none opacity-60"
                              : ""
                          }`}
                          href={isDisabled ? undefined : `tel:${phone}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <span class="material-symbols-outlined">call</span>
                          {phone || t("reservations.general.no_phone")}
                        </ExternalLink>
                      );
                    })()}
                    {(() => {
                      const email =
                        props.request.party_email ||
                        props.request.customer_email;
                      const isDisabled = !email;
                      return (
                        <ExternalLink
                          class={`btn btn-secondary w-full justify-start gap-2 ${
                            isDisabled
                              ? "btn-disabled pointer-events-none opacity-60"
                              : ""
                          }`}
                          href={isDisabled ? undefined : `mailto:${email}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <span class="material-symbols-outlined">mail</span>
                          {email || t("reservations.general.no_email")}
                        </ExternalLink>
                      );
                    })()}
                  </div>
                </div>

                {(() => {
                  const requestedTags =
                    (props.request as any).requested_tags || [];
                  return requestedTags.length > 0 ? (
                    <div class="rounded-2xl border border-base-300 bg-base-200/60 p-4 space-y-2">
                      <div class="text-xs font-semibold text-base-content/45 uppercase tracking-wider">
                        {t("reservations.details.requested_experiences")}
                      </div>
                      <div class="flex flex-wrap gap-1.5">
                        {requestedTags.map((tag: any) => (
                          <span
                            key={tag.id}
                            class="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-current/15"
                            style={{
                              backgroundColor: tag.color
                                ? `${tag.color}20`
                                : "oklch(var(--b3))",
                              color: tag.color || undefined,
                            }}
                          >
                            {tag.icon && (
                              <span class="material-symbols-outlined text-sm">
                                {tag.icon}
                              </span>
                            )}
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null;
                })()}

                {props.request.notes && (
                  <div class="rounded-2xl bg-amber-500/10 border border-amber-500/20 p-4 space-y-2">
                    <div class="flex items-center gap-2 text-amber-500">
                      <span class="material-symbols-outlined">chat</span>
                      <span class="text-xs uppercase tracking-wide font-semibold">
                        {t("reservations.general.guest_notes")}
                      </span>
                    </div>
                    <p class="text-xs text-base-content/80 leading-relaxed">
                      {props.request.notes}
                    </p>
                  </div>
                )}

                {/* Decline Reason */}
                <div class="rounded-2xl border border-base-300 bg-base-200/60 p-4 l-box-shadow space-y-3">
                  <div class="flex items-center justify-between">
                    <h4 class="text-xs font-semibold text-accent uppercase tracking-wide">
                      {t("reservations.reject.reason_for_declining")}
                    </h4>
                    <span class="badge badge-outline badge-sm">
                      {t("reservations.reject.optional")}
                    </span>
                  </div>
                  <div class="space-y-2">
                    {declineReasons.value.map((reason) => {
                      const isSelected = selectedReason.value === reason.id;
                      return (
                        <button
                          key={reason.id}
                          type="button"
                          class={`w-full p-3 rounded-lg border text-left transition-all flex items-center gap-3 ${
                            isSelected
                              ? "border-rose-500 bg-rose-500/10"
                              : "border-base-300 hover:border-base-content/30 hover:bg-base-200/50"
                          }`}
                          onClick={() =>
                            (selectedReason.value = isSelected
                              ? null
                              : reason.id)
                          }
                          disabled={submitting.value}
                        >
                          <div
                            class={`w-8 h-8 rounded-full flex items-center justify-center ${
                              isSelected ? "bg-rose-500/20" : "bg-base-200"
                            }`}
                          >
                            <span
                              class={`material-symbols-outlined text-lg ${
                                isSelected
                                  ? "text-rose-500"
                                  : "text-base-content/50"
                              }`}
                            >
                              {reason.icon}
                            </span>
                          </div>
                          <span
                            class={`text-sm ${
                              isSelected
                                ? "text-rose-400"
                                : "text-base-content/80"
                            }`}
                          >
                            {reason.label}
                          </span>
                          {isSelected && (
                            <span class="material-symbols-outlined text-rose-500 ml-auto">
                              check
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  <div class="mt-2">
                    <textarea
                      class="textarea textarea-bordered w-full bg-base-200 resize-none"
                      rows={3}
                      placeholder={t(
                        "reservations.reject.add_note_placeholder",
                      )}
                      value={customMessage.value}
                      onInput={(e) =>
                        (customMessage.value = (
                          e.target as HTMLTextAreaElement
                        ).value)
                      }
                      disabled={submitting.value}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div class="flex-shrink-0 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] border-t border-base-300 bg-base-100/90 backdrop-blur">
              <div class="flex gap-2 w-full">
                <button
                  type="button"
                  class="btn btn-sm btn-secondary flex-1 min-w-0"
                  onClick={handleClose}
                  disabled={submitting.value}
                >
                  <span class="truncate">
                    {t("reservations.general.cancel")}
                  </span>
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-error flex-1 min-w-0"
                  onClick={handleReject}
                  disabled={submitting.value}
                >
                  {submitting.value ? (
                    <span class="loading loading-spinner loading-xs"></span>
                  ) : (
                    <span class="material-symbols-outlined text-sm flex-shrink-0">
                      close
                    </span>
                  )}
                  <span class="truncate">
                    {submitting.value
                      ? t("reservations.reject.declining")
                      : t("reservations.general.decline")}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </RightSheet>
      );
    },
  });
